/**
 * Mobile (portrait) tour — the ai-first story retold for a phone:
 * chat → AI demo → pop-out preview round-trip → edit element → recap.
 *
 * The preview step can't overlay the pop-out tab, so it watches the
 * broadcast channel instead: the user's element tap over there arrives as an
 * elementClick message, and the step advances itself when they swipe back.
 */
import type { StepType } from "@reactour/tour";
import { useTour } from "@reactour/tour";
import { strings, text } from "@quickpoint.me/shared";
import { useEffect, useRef } from "react";
import { authorReceiveBC } from "../../../messageBus";
import { goToPage } from "../../../editor/events";
import TourStepContent from "../ai-first/wrapper";
import {
	clearTyping,
	injectDemoPresentation,
	preloadDemoImages,
	simulateElementClick,
	simulateInlineEdit,
	simulateTyping,
	streamMockAiResponse,
	switchToLevel,
} from "../ai-first/helpers";

/** same demo deck as ai-first: "title: Solar Power" on line 4, page 2 */
const DEMO_TITLE_LINE = 4;
const DEMO_TITLE_PAGE = 2;

/**
 * Step content that completes itself: an elementClick from the pop-out marks
 * the round-trip done, and returning to this tab (visibilitychange) advances
 * the tour. Next still works as a manual escape hatch.
 */
function PreviewRoundTripContent() {
	const { setCurrentStep } = useTour();
	const tapped = useRef(false);

	useEffect(() => {
		const bc = authorReceiveBC;
		if (!bc) return;
		const advance = () =>
			setCurrentStep((s) => (typeof s === "number" ? s + 1 : s));
		const onMessage = (e: MessageEvent) => {
			if (e.data?.action === strings.messages.elementClick) {
				tapped.current = true;
				if (document.visibilityState === "visible") advance();
			}
		};
		const onVisibility = () => {
			if (document.visibilityState === "visible" && tapped.current) advance();
		};
		bc.addEventListener("message", onMessage);
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			bc.removeEventListener("message", onMessage);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [setCurrentStep]);

	return (
		<TourStepContent
			description={() => (
				<span>
					{text.tourSteps.mobilePreviewTap}
					<br />
					<br />
					{text.tourSteps.mobilePreviewWaiting}
				</span>
			)}
		/>
	);
}

export const mobileTourSteps = (hostId: string): StepType[] => [
	// ── Welcome ─────────────────────────────────────────────
	{
		selector: "window",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.welcomeIntro}
						<br />
						<br />
						{text.tourSteps.welcomeDescription}
					</span>
				)}
			/>
		),
		position: "center",
		action: () => {
			switchToLevel(1);
			preloadDemoImages();
		},
	},
	{
		selector: "#tab-chat",
		content: () => (
			<TourStepContent description={() => text.tourSteps.chatTabIntro} />
		),
	},
	{
		selector: ".chat-codemirror",
		content: () => (
			<TourStepContent description={() => text.tourSteps.chatInputHint} />
		),
	},
	// ── AI demo ─────────────────────────────────────────────
	{
		selector: ".chat-codemirror",
		content: () => (
			<TourStepContent description={() => text.tourSteps.demoTyping} />
		),
		action: () => {
			// no focusChatInput() here — focusing pops the mobile keyboard over
			// the tour card; the simulated typing needs no caret. Blur anything
			// already focused so an open keyboard closes.
			(document.activeElement as HTMLElement | null)?.blur?.();
			simulateTyping(text.tourSteps.demoPrompt);
		},
	},
	{
		selector: ".chat-codemirror",
		content: () => (
			<TourStepContent description={() => text.tourSteps.demoAiPlans} />
		),
		action: () => {
			clearTyping();
			streamMockAiResponse(text.tourSteps.demoPrompt, text.tourSteps.demoMockRationale);
		},
	},
	{
		selector: ".cm-create-prompt",
		content: () => (
			<TourStepContent description={() => text.tourSteps.demoCreateButton} />
		),
	},
	{
		selector: "window",
		content: () => (
			<TourStepContent description={() => text.tourSteps.mobileDeckReady} />
		),
		position: "center",
		action: () => {
			injectDemoPresentation(hostId);
		},
	},
	// ── Preview round-trip (self-completing) ────────────────
	{
		selector: '[data-testid="mobile-preview-control"]',
		content: () => <PreviewRoundTripContent />,
		// the user must be able to tap the real Preview button
		stepInteraction: true,
	},
	// ── Edit element ────────────────────────────────────────
	{
		selector: "#panel-edit",
		content: () => (
			<TourStepContent description={() => text.tourSteps.mobileTappedElement} />
		),
		action: () => {
			// normalise onto the demo title so the scripted edit always fits,
			// whichever element the user tapped in the pop-out; the delay lets
			// the return-to-tab renders settle first so this click wins
			setTimeout(() => {
				simulateElementClick(DEMO_TITLE_LINE, DEMO_TITLE_PAGE, "title");
				setTimeout(() => goToPage(hostId, "QP", DEMO_TITLE_PAGE), 300);
			}, 200);
		},
	},
	{
		selector: "#panel-edit",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.editElementWatch}
						<br />
						<br />
						{text.tourSteps.editElementRealtime}
					</span>
				)}
			/>
		),
		action: () => {
			// "title: Solar Power" → replace "Solar Power" (starts at col 7)
			simulateInlineEdit(text.tourSteps.editElementReplacement, 7, hostId, DEMO_TITLE_PAGE);
		},
	},
	{
		selector: "window",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.editElementPerfectFor}
						<br />
						<br />
						{text.tourSteps.editElementFixTypos}
						<br />
						{text.tourSteps.editElementAdjustWording}
						<br />
						{text.tourSteps.editElementChangeElements}
					</span>
				)}
			/>
		),
		position: "center",
		action: () => {
			clearTyping();
		},
	},
	// ── What's next ─────────────────────────────────────────
	{
		selector: "window",
		content: () => (
			<TourStepContent
				description={() => <strong>{text.tourSteps.yourWorkflow}</strong>}
			/>
		),
		position: "center",
	},
	{
		selector: "#tab-chat",
		content: () => (
			<TourStepContent
				description={() => <strong>{text.tourSteps.startWithChat}</strong>}
			/>
		),
		action: () => {
			switchToLevel(1);
		},
	},
	{
		selector: "#tab-edit-element",
		content: () => (
			<TourStepContent
				description={() => <strong>{text.tourSteps.useEditElement}</strong>}
			/>
		),
	},
	{
		selector: "#tab-structure",
		content: () => (
			<TourStepContent
				description={() => <strong>{text.tourSteps.masterStructure}</strong>}
			/>
		),
	},
	{
		selector: "#control-stories",
		content: () => (
			<TourStepContent description={() => text.tourSteps.storiesSaved} />
		),
	},
	{
		selector: '[data-testid="share-control"]',
		content: () => (
			<TourStepContent description={() => text.tourSteps.shareWithLink} />
		),
	},
	{
		selector: "window",
		content: () => (
			<TourStepContent description={() => text.tourSteps.mobileTwoTabs} />
		),
		position: "center",
	},
	{
		selector: "window",
		content: () => (
			<TourStepContent
				description={() => (
					<span>
						{text.tourSteps.readyToGo}
						<br />
						<br />
						{text.tourSteps.startByTyping}
					</span>
				)}
			/>
		),
		position: "center",
		action: () => {
			switchToLevel(1);
		},
	},
];
